import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import moment from 'moment-timezone';
import { BenefitUsageFilters, BenefitUsageRow, exportBenefitUsageThunk } from '@spike/memberships-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import {
    benefitTypeLabel,
    formatDiscount,
    itemTypeLabel,
    planTypeLabel,
    serviceCategoryLabel
} from './benefitUsageLabels';

interface BenefitUsageExportCSVProps {
    filters: BenefitUsageFilters;
}

const csvHeaders = [
    'Date',
    'Time',
    'Client',
    'Pet',
    'Benefit',
    'Item Type',
    'Service Category',
    'Membership / Package',
    'Plan Type',
    'Usage Type',
    'Discount Amount',
    'Quantity'
];

// ExportCSV writes without enclosing characters and with ';' as separator, so these would break the columns.
const cleanCell = (value: string | null | undefined) => (value ?? '').replace(/[;\r\n]+/g, ' ').trim();

const BenefitUsageExportCSV: React.FC<BenefitUsageExportCSVProps> = ({ filters }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const csvRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<string[][]>([]);

    useNonInitialEffect(() => {
        if (records.length > 0) {
            csvRef.current?.export();
        }
    }, [records]);

    const toRecord = (row: BenefitUsageRow) => {
        const usedAt = moment.tz(row.usedAt, timeZone);

        return [
            usedAt.format('YYYY-MM-DD'),
            usedAt.format('h:mm A'),
            row.clientName,
            row.petName,
            row.benefitName,
            itemTypeLabel(row.itemType),
            serviceCategoryLabel(row.serviceCategory),
            row.membershipName,
            planTypeLabel(row.enrollmentType),
            benefitTypeLabel(row.benefitKind),
            row.benefitKind === 'discount' ? formatDiscount(row.discountAmount) : '',
            String(row.quantity)
        ].map(cleanCell);
    };

    const exportHandler = async () => {
        setLoading(true);
        try {
            const rows: BenefitUsageRow[] = await dispatch(exportBenefitUsageThunk(apiClientWrapper, filters));
            setRecords(rows.map(toRecord));
        } catch (error) {
            // the thunk already shows the error alert
        } finally {
            setLoading(false);
        }
    };

    const fileName = `Benefit_Usage_${marketplace.basics.businessName.replace(/\s+/g, '')}_${moment().format(
        'YYYYMMDD_HHmm'
    )}.csv`;

    return (
        <ExportCSV
            ref={csvRef}
            headers={csvHeaders}
            records={records}
            filename={fileName}
            exporting={loading}
            onClick={exportHandler}
        />
    );
};

export default BenefitUsageExportCSV;
